import { useLang } from "@/lib/lang";
import { FileText, FileImage, FileType2, FileCode2, Trash2, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

const ICONS = {
  pdf: FileText,
  docx: FileType2,
  image: FileImage,
  text: FileCode2,
};

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1000) return `${n}`;
  if (n < 1000000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1000000).toFixed(2)}M`;
}

export default function DocumentCard({ doc, active, checked, multiSelectMode, onSelect, onDelete }) {
  const { t, lang } = useLang();
  const Icon = ICONS[doc.file_type] || FileText;
  const ChevronEnd = lang === "ar" ? ChevronLeft : ChevronRight;

  return (
    <div
      data-testid={`doc-card-${doc.id}`}
      onClick={() => onSelect?.(doc)}
      className={[
        "group cursor-pointer transition-colors duration-150 grid-border-b",
        active ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50 text-slate-900",
      ].join(" ")}
    >
      <div className="p-4 flex items-start gap-3">
        {multiSelectMode && (
          <div className={[
            "w-5 h-5 flex items-center justify-center flex-shrink-0 grid-border mt-1",
            checked ? "bg-slate-900 text-white border-slate-900" : "bg-white",
          ].join(" ")}>
            {checked && <Check size={12} />}
          </div>
        )}
        <div className={[
          "w-9 h-9 flex items-center justify-center flex-shrink-0 grid-border",
          active ? "border-white/30 bg-white/10" : "border-slate-200 bg-slate-50",
        ].join(" ")}>
          <Icon size={16} strokeWidth={1.6} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{doc.filename}</div>
          <div className={["mt-1 flex items-center gap-3 text-[11px]", active ? "text-white/70" : "text-slate-500"].join(" ")}>
            <span className="font-mono-tight">{doc.pages} {t("pages")}</span>
            <span>·</span>
            <span className="font-mono-tight">{formatBytes(doc.chars)} {t("chars")}</span>
            <span>·</span>
            <span className="uppercase tracking-wider">{doc.language}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            data-testid={`doc-delete-${doc.id}`}
            size="icon"
            variant="ghost"
            className={["h-7 w-7 rounded-none", active ? "text-white hover:bg-white/20" : "text-slate-500 hover:text-red-600"].join(" ")}
            onClick={(e) => { e.stopPropagation(); onDelete?.(doc); }}
          >
            <Trash2 size={13} />
          </Button>
          <ChevronEnd size={14} className={active ? "text-white/70" : "text-slate-400"} />
        </div>
      </div>
    </div>
  );
}
